import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Empty,
  Form,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Divider,
  Typography,
} from "antd";
import { Apartamento } from "../../interfaces/Apartamento";
import { Cota } from "../../interfaces/Cota";
import { DiagnosticoOperacionalIrregularidade } from "../../interfaces/DiagnosticoOperacionalIrregularidade";
import { Empreendimento } from "../../interfaces/Empreendimento";
import { apartamentoByEmpreendimentoService } from "../../services/apartamentoByEmpreendimentoService";
import { atualizarCotaUsuarioService } from "../../services/atualizarCotaUsuarioService";
import { atualizarPeriodoUsuario } from "../../services/atualizarPeriodoUsuarioService";
import { buscarCotaUsuarioByUsuarioIdService } from "../../services/buscarCotaUsuarioByUsuarioIdService";
import { buscarCotasByAptoService } from "../../services/buscarCotasByAptoService";
import { buscarEmpreendimentosService } from "../../services/buscarEmpreendimentosService";
import { buscarPeriodosByCotaIdService } from "../../services/buscarPeriodosByCotaId";
import { buscarPeriodosByCotaUsuario } from "../../services/buscarPeriodosByCotaUsuarioService";
import { sincronizarPeriodosCotaUsuarioService } from "../../services/sincronizarPeriodosCotaUsuarioService";

type CotaUsuarioDetalhada = {
  id: number;
  cotaId?: number;
  CotaTable?: {
    id?: number;
    numeroCota?: number | string;
    AptoTable?: {
      id?: number;
      nome?: string;
      capacidade?: number | null;
      quartos?: number | null;
      banheiros?: number | null;
      metragem?: number | null;
      EmpreendimentoTable?: {
        id?: number;
        nome?: string;
        cronograma?: number | null;
      };
    };
  };
};

type AjustarCotaModalProps = {
  open: boolean;
  irregularidade: DiagnosticoOperacionalIrregularidade | null;
  onCancel: () => void;
  onSuccess: () => void;
};

type PeriodoUsuarioDetalhado = {
  id: number;
  periodoId?: number;
  PeriodoTable?: {
    id?: number;
    checkIn?: string;
    checkOut?: string;
  };
};

type PeriodoCotaDetalhado = {
  id: number;
  checkIn?: string;
  checkOut?: string;
};

type PeriodoVinculo = {
  periodoUsuarioId: number;
  periodoAtualId?: number;
  antigoCheckIn: string;
  antigoCheckOut: string;
  selectedPeriodoId?: number;
  migrar?: boolean;
};

const resolveIrregularidadePrincipal = (
  irregularidade: Partial<DiagnosticoOperacionalIrregularidade> | null | undefined
) => {
  if (irregularidade?.irregularidadePrincipal) {
    return irregularidade.irregularidadePrincipal;
  }

  const irregularidadesRaw = irregularidade?.irregularidades;
  const irregularidades = Array.isArray(irregularidadesRaw)
    ? irregularidadesRaw.filter(
        (item): item is string => typeof item === "string"
      )
    : [];

  const prioridade = ["PEU", "EP", "AP"];
  const irregularidadePrioritaria = prioridade.find((item) =>
    irregularidades.includes(item)
  );

  return irregularidadePrioritaria ?? irregularidades[0] ?? "";
};

const normalizeCotaUsuarioResponse = (
  response: unknown
): CotaUsuarioDetalhada | null => {
  if (!response || typeof response !== "object") {
    return null;
  }

  const responseAsRecord = response as Record<string, unknown>;
  const cotaUsuario =
    "CotasUsuariosTable" in responseAsRecord &&
    responseAsRecord.CotasUsuariosTable &&
    typeof responseAsRecord.CotasUsuariosTable === "object"
      ? (responseAsRecord.CotasUsuariosTable as Record<string, unknown>)
      : responseAsRecord;

  const cotaTable =
    "CotaTable" in cotaUsuario && cotaUsuario.CotaTable
      ? (cotaUsuario.CotaTable as CotaUsuarioDetalhada["CotaTable"])
      : undefined;

  const cotaId =
    typeof cotaUsuario.cotaId === "number"
      ? cotaUsuario.cotaId
      : cotaTable?.id;

  return {
    id: typeof cotaUsuario.id === "number" ? cotaUsuario.id : 0,
    cotaId,
    CotaTable: cotaTable,
  };
};

const normalizePeriodosUsuarioResponse = (
  response: unknown
): PeriodoUsuarioDetalhado[] => {
  if (Array.isArray(response)) {
    return response as PeriodoUsuarioDetalhado[];
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const responseAsRecord = response as Record<string, unknown>;
  const possibleCollections = [
    responseAsRecord.data,
    responseAsRecord.items,
    responseAsRecord.rows,
    responseAsRecord.periodos,
    responseAsRecord.periodosUsuario,
  ];

  const collection = possibleCollections.find((value) => Array.isArray(value));

  if (Array.isArray(collection)) {
    return collection as PeriodoUsuarioDetalhado[];
  }

  if ("id" in responseAsRecord) {
    return [response as PeriodoUsuarioDetalhado];
  }

  return [];
};

const normalizePeriodosCotaResponse = (
  response: unknown
): PeriodoCotaDetalhado[] => {
  if (Array.isArray(response)) {
    return response as PeriodoCotaDetalhado[];
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const responseAsRecord = response as Record<string, unknown>;
  const possibleCollections = [
    responseAsRecord.data,
    responseAsRecord.items,
    responseAsRecord.rows,
    responseAsRecord.periodos,
  ];

  const collection = possibleCollections.find((value) => Array.isArray(value));

  if (Array.isArray(collection)) {
    return collection as PeriodoCotaDetalhado[];
  }

  if ("id" in responseAsRecord) {
    return [response as PeriodoCotaDetalhado];
  }

  return [];
};

const infoValue = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === "" ? "-" : value;

const formatCronogramaValue = (value?: number | null) => {
  if (value === 1) {
    return "Fixo";
  }

  if (value === 0) {
    return "Sem cronograma";
  }

  return "-";
};

const formatDateValue = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  if (value.includes("/")) {
    return value;
  }

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return `${day}/${month}/${year}`;
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.format("DD/MM/YYYY") : value;
};

const buildPeriodoVinculos = (
  periodosUsuario: PeriodoUsuarioDetalhado[],
  periodosNovaCota: PeriodoCotaDetalhado[],
  previousMappings: PeriodoVinculo[] = []
) => {
  const usedPeriodoIds = new Set<number>();

  return periodosUsuario.map((periodoUsuario) => {
    const previousMapping = previousMappings.find(
      (mapping) => mapping.periodoUsuarioId === periodoUsuario.id
    );
    const periodoAtual = periodoUsuario.PeriodoTable;
    const antigoCheckIn = formatDateValue(periodoAtual?.checkIn);
    const antigoCheckOut = formatDateValue(periodoAtual?.checkOut);

    let selectedPeriodoId = previousMapping?.selectedPeriodoId;
    const selectedPeriodoExiste = periodosNovaCota.some(
      (periodo) => periodo.id === selectedPeriodoId
    );

    if (!selectedPeriodoExiste) {
      selectedPeriodoId = undefined;
    }

    if (!selectedPeriodoId) {
      const periodoCompativel = periodosNovaCota.find((periodo) => {
        if (!periodo.id || usedPeriodoIds.has(periodo.id)) {
          return false;
        }

        return (
          formatDateValue(periodo.checkIn) === antigoCheckIn &&
          formatDateValue(periodo.checkOut) === antigoCheckOut
        );
      });

      selectedPeriodoId = periodoCompativel?.id;
    }

    if (selectedPeriodoId) {
      usedPeriodoIds.add(selectedPeriodoId);
    }

    return {
      periodoUsuarioId: periodoUsuario.id,
      periodoAtualId: periodoUsuario.periodoId ?? periodoAtual?.id,
      antigoCheckIn,
      antigoCheckOut,
      selectedPeriodoId,
      migrar: previousMapping?.migrar ?? false,
    };
  });
};

export default function AjustarCotaModal({
  open,
  irregularidade,
  onCancel,
  onSuccess,
}: AjustarCotaModalProps) {
  const irregularidadePrincipal = resolveIrregularidadePrincipal(irregularidade);
  const isAjustePeu = irregularidadePrincipal === "PEU";
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isApartamentosLoading, setIsApartamentosLoading] = useState(false);
  const [isCotasLoading, setIsCotasLoading] = useState(false);
  const [isPeriodosUsuarioLoading, setIsPeriodosUsuarioLoading] = useState(false);
  const [isPeriodosNovaCotaLoading, setIsPeriodosNovaCotaLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cotaUsuarioAtual, setCotaUsuarioAtual] =
    useState<CotaUsuarioDetalhada | null>(null);
  const [empreendimentos, setEmpreendimentos] = useState<Empreendimento[]>([]);
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [cotas, setCotas] = useState<Cota[]>([]);
  const [periodosUsuario, setPeriodosUsuario] = useState<
    PeriodoUsuarioDetalhado[]
  >([]);
  const [periodosNovaCota, setPeriodosNovaCota] = useState<PeriodoCotaDetalhado[]>(
    []
  );
  const [periodoVinculos, setPeriodoVinculos] = useState<PeriodoVinculo[]>([]);
  const hasPrefilledCurrentSelectionRef = useRef(false);
  const [showPeriodosSection, setShowPeriodosSection] = useState(false);
  const [selectedEmpreendimentoId, setSelectedEmpreendimentoId] = useState<
    number | undefined
  >(undefined);
  const [selectedApartamentoId, setSelectedApartamentoId] = useState<
    number | undefined
  >(undefined);
  const [selectedCotaId, setSelectedCotaId] = useState<number | undefined>(
    undefined
  );

 

  useEffect(() => {
    if (!open || !irregularidade) {
      setLoadError(null);
      setSaveError(null);
      setCotaUsuarioAtual(null);
      setEmpreendimentos([]);
      setApartamentos([]);
      setCotas([]);
      setPeriodosUsuario([]);
      setPeriodosNovaCota([]);
      setPeriodoVinculos([]);
      hasPrefilledCurrentSelectionRef.current = false;
      setShowPeriodosSection(false);
      setSelectedEmpreendimentoId(undefined);
      setSelectedApartamentoId(undefined);
      setSelectedCotaId(undefined);
      return;
    }

    let isMounted = true;

    const carregarDadosIniciais = async () => {
      setIsInitialLoading(true);
      setLoadError(null);
      setSaveError(null);

      try {
        setIsPeriodosUsuarioLoading(true);

        const [
          cotaUsuarioResponse,
          empreendimentosResponse,
          periodosUsuarioResponse,
        ] = await Promise.all([
          buscarCotaUsuarioByUsuarioIdService(
            irregularidade.usuarioId,
            irregularidade.cotaUsuarioId
          ),
          buscarEmpreendimentosService(),
          buscarPeriodosByCotaUsuario(irregularidade.cotaUsuarioId),
        ]);

        if (!isMounted) {
          return;
        }

        const cotaUsuarioData = normalizeCotaUsuarioResponse(cotaUsuarioResponse);

        if (!cotaUsuarioData) {
          setLoadError("Nao foi possivel carregar a cota atual desta CTU.");
          return;
        }

        setCotaUsuarioAtual(cotaUsuarioData);
        setEmpreendimentos(
          Array.isArray(empreendimentosResponse) ? empreendimentosResponse : []
        );
        hasPrefilledCurrentSelectionRef.current = false;

        if (isAjustePeu) {
          setSelectedEmpreendimentoId(undefined);
          setSelectedApartamentoId(undefined);
          setSelectedCotaId(cotaUsuarioData.cotaId);
          hasPrefilledCurrentSelectionRef.current = true;
        } else {
          setSelectedEmpreendimentoId(
            cotaUsuarioData.CotaTable?.AptoTable?.EmpreendimentoTable?.id
          );
        }

        const periodosUsuarioData =
          normalizePeriodosUsuarioResponse(periodosUsuarioResponse);
        setPeriodosUsuario(periodosUsuarioData);
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setLoadError("Nao foi possivel carregar os dados para ajuste.");
        }
      } finally {
        if (isMounted) {
          setIsInitialLoading(false);
          setIsPeriodosUsuarioLoading(false);
        }
      }
    };

    carregarDadosIniciais();

    return () => {
      isMounted = false;
    };
  }, [open, irregularidade, isAjustePeu]);

  useEffect(() => {
    if (!open || isAjustePeu) {
      return;
    }

    const empreendimentoAtualId =
      cotaUsuarioAtual?.CotaTable?.AptoTable?.EmpreendimentoTable?.id;
    const shouldPrefillApartamento =
      !hasPrefilledCurrentSelectionRef.current &&
      !!empreendimentoAtualId &&
      selectedEmpreendimentoId === empreendimentoAtualId;

    setApartamentos([]);
    setCotas([]);
    setPeriodosNovaCota([]);
    setPeriodoVinculos([]);
    setSelectedApartamentoId(undefined);
    setSelectedCotaId(undefined);

    if (!selectedEmpreendimentoId) {
      return;
    }

    let isMounted = true;

    const carregarApartamentos = async () => {
      setIsApartamentosLoading(true);
      setLoadError(null);

      try {
        const response = await apartamentoByEmpreendimentoService(
          selectedEmpreendimentoId
        );

        if (!isMounted) {
          return;
        }

        const apartamentosData = Array.isArray(response) ? response : [];
        setApartamentos(apartamentosData);

        if (shouldPrefillApartamento) {
          const apartamentoAtualId = cotaUsuarioAtual?.CotaTable?.AptoTable?.id;
          const apartamentoAtualExiste = apartamentosData.some(
            (apartamento) => apartamento.id === apartamentoAtualId
          );

          setSelectedApartamentoId(
            apartamentoAtualExiste ? apartamentoAtualId : undefined
          );
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setLoadError(
            "Nao foi possivel carregar os apartamentos do empreendimento."
          );
        }
      } finally {
        if (isMounted) {
          setIsApartamentosLoading(false);
        }
      }
    };

    carregarApartamentos();

    return () => {
      isMounted = false;
    };
  }, [
    open,
    isAjustePeu,
    selectedEmpreendimentoId,
    cotaUsuarioAtual,
  ]);

  useEffect(() => {
    if (!open || isAjustePeu) {
      return;
    }

    const apartamentoAtualId = cotaUsuarioAtual?.CotaTable?.AptoTable?.id;
    const shouldPrefillCota =
      !hasPrefilledCurrentSelectionRef.current &&
      !!apartamentoAtualId &&
      selectedApartamentoId === apartamentoAtualId;

    setCotas([]);
    setPeriodosNovaCota([]);
    setPeriodoVinculos([]);
    setSelectedCotaId(undefined);

    if (!selectedApartamentoId) {
      return;
    }

    let isMounted = true;

    const carregarCotas = async () => {
      setIsCotasLoading(true);
      setLoadError(null);

      try {
        const response = await buscarCotasByAptoService(selectedApartamentoId);

        if (!isMounted) {
          return;
        }

        const cotasData = Array.isArray(response) ? response : [];
        setCotas(cotasData);

        if (shouldPrefillCota) {
          const cotaAtualId = cotaUsuarioAtual?.cotaId;
          const cotaAtualExiste = cotasData.some((cota) => cota.id === cotaAtualId);

          setSelectedCotaId(cotaAtualExiste ? cotaAtualId : undefined);
          hasPrefilledCurrentSelectionRef.current = true;
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setLoadError("Nao foi possivel carregar as cotas do apartamento.");
        }
      } finally {
        if (isMounted) {
          setIsCotasLoading(false);
        }
      }
    };

    carregarCotas();

    return () => {
      isMounted = false;
    };
  }, [
    open,
    isAjustePeu,
    selectedApartamentoId,
    cotaUsuarioAtual,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPeriodosNovaCota([]);
    setPeriodoVinculos([]);

    if (!selectedCotaId) {
      return;
    }

    let isMounted = true;

    const carregarPeriodosNovaCota = async () => {
      setIsPeriodosNovaCotaLoading(true);
      setLoadError(null);

      try {
        const response = await buscarPeriodosByCotaIdService(selectedCotaId);

        if (!isMounted) {
          return;
        }

        const periodosNovaCotaData = normalizePeriodosCotaResponse(response);

        setPeriodosNovaCota(periodosNovaCotaData);
        setPeriodoVinculos(
          buildPeriodoVinculos(periodosUsuario, periodosNovaCotaData)
        );
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setLoadError("Nao foi possivel carregar os periodos da nova cota.");
        }
      } finally {
        if (isMounted) {
          setIsPeriodosNovaCotaLoading(false);
        }
      }
    };

    carregarPeriodosNovaCota();

    return () => {
      isMounted = false;
    };
  }, [open, selectedCotaId, periodosUsuario]);

  const apartamentoAtual = cotaUsuarioAtual?.CotaTable?.AptoTable;
  const empreendimentoAtual = apartamentoAtual?.EmpreendimentoTable;

  const empreendimentoSelecionado = useMemo(
    () =>
      empreendimentos.find(
        (empreendimento) => empreendimento.id === selectedEmpreendimentoId
      ),
    [empreendimentos, selectedEmpreendimentoId]
  );

  const apartamentoSelecionado = useMemo(
    () =>
      apartamentos.find(
        (apartamento) => apartamento.id === selectedApartamentoId
      ),
    [apartamentos, selectedApartamentoId]
  );

  const cotaSelecionada = useMemo(
    () => cotas.find((cota) => cota.id === selectedCotaId),
    [cotas, selectedCotaId]
  );

  const periodosNovaCotaById = useMemo(() => {
    return periodosNovaCota.reduce<Record<number, PeriodoCotaDetalhado>>(
      (accumulator, periodo) => {
        if (periodo.id) {
          accumulator[periodo.id] = periodo;
        }

        return accumulator;
      },
      {}
    );
  }, [periodosNovaCota]);

  const duplicatedPeriodoIds = useMemo(() => {
    const counter = new Map<number, number>();

    periodoVinculos.forEach((vinculo) => {
      if (!vinculo.selectedPeriodoId || vinculo.migrar) {
        return;
      }

      counter.set(
        vinculo.selectedPeriodoId,
        (counter.get(vinculo.selectedPeriodoId) ?? 0) + 1
      );
    });

    return new Set(
      Array.from(counter.entries())
        .filter(([, count]) => count > 1)
        .map(([periodoId]) => periodoId)
    );
  }, [periodoVinculos]);

  const periodosPendentes = periodoVinculos.filter(
    (vinculo) => !vinculo.selectedPeriodoId && !vinculo.migrar
  ).length;
  const periodosParaMigrar = periodoVinculos.filter((vinculo) => vinculo.migrar);
  const migracoesInvalidas = periodosParaMigrar.filter(
    (vinculo) => !vinculo.periodoAtualId
  ).length;
  const isCotaAlterada = selectedCotaId !== cotaUsuarioAtual?.cotaId;
  const shouldBypassPeriodoAdjustment =
    !isAjustePeu && empreendimentoSelecionado?.cronograma === 0;
  const shouldRequirePeriodoMapping =
    isAjustePeu || !shouldBypassPeriodoAdjustment;
  const shouldShowPeriodosSection = isAjustePeu
    ? showPeriodosSection
    : !!selectedCotaId;

  const hasDuplicatedPeriodos = duplicatedPeriodoIds.size > 0;
  const hasPeriodoMappingIssues =
    shouldRequirePeriodoMapping &&
    periodosUsuario.length > 0 &&
    (!!periodosPendentes ||
      hasDuplicatedPeriodos ||
      !!migracoesInvalidas ||
      !selectedCotaId);

  const canSubmit =
    !!selectedCotaId &&
    (isAjustePeu || isCotaAlterada) &&
    (!isAjustePeu || showPeriodosSection) &&
    !isInitialLoading &&
    !isApartamentosLoading &&
    !isCotasLoading &&
    !isPeriodosUsuarioLoading &&
    !isPeriodosNovaCotaLoading &&
    (!shouldRequirePeriodoMapping || !hasPeriodoMappingIssues) &&
    !loadError;

  const handlePeriodoChange = (
    periodoUsuarioId: number,
    periodoId?: number
  ) => {
    setPeriodoVinculos((currentValue) =>
      currentValue.map((vinculo) =>
            vinculo.periodoUsuarioId === periodoUsuarioId
              ? {
                  ...vinculo,
                  migrar: false,
                  selectedPeriodoId: periodoId,
                }
              : vinculo
      )
    );
    setSaveError(null);
  };

  const handleMigrarChange = (
    periodoUsuarioId: number,
    checked: boolean
  ) => {
    setPeriodoVinculos((currentValue) =>
      currentValue.map((vinculo) =>
        vinculo.periodoUsuarioId === periodoUsuarioId
          ? {
              ...vinculo,
              migrar: checked,
              selectedPeriodoId: checked ? undefined : vinculo.selectedPeriodoId,
            }
          : vinculo
      )
    );
    setSaveError(null);
  };

  const handleSalvar = async () => {
    if (!irregularidade || !selectedCotaId) {
      return;
    }

    if (!isAjustePeu && !isCotaAlterada) {
      setSaveError("Selecione uma cota diferente da atual para salvar.");
      return;
    }

    if (shouldRequirePeriodoMapping && hasDuplicatedPeriodos) {
      setSaveError(
        "Cada periodo novo deve ser vinculado a apenas um periodo antigo."
      );
      return;
    }

    if (shouldRequirePeriodoMapping && periodosPendentes > 0) {
      setSaveError("Defina o vinculo de todos os periodos antes de salvar.");
      return;
    }

    if (shouldRequirePeriodoMapping && migracoesInvalidas > 0) {
      setSaveError(
        "Um ou mais periodos marcados para migracao nao possuem periodo base valido."
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      if (isCotaAlterada) {
        const response = await atualizarCotaUsuarioService(
          irregularidade.cotaUsuarioId,
          { cotaId: selectedCotaId }
        );

        if (!response) {
          setSaveError("Nao foi possivel salvar o ajuste desta CTU.");
          return;
        }
      }

      if (periodosParaMigrar.length > 0) {
        const periodoIds = periodosParaMigrar
          .map((vinculo) => vinculo.periodoAtualId)
          .filter((periodoId): periodoId is number => typeof periodoId === "number");

        const responseSincronizacao =
          await sincronizarPeriodosCotaUsuarioService(
            irregularidade.cotaUsuarioId,
            { periodoIds }
          );

        if (!responseSincronizacao) {
          setSaveError("Nao foi possivel migrar os periodos marcados.");
          return;
        }
      }

      if (shouldRequirePeriodoMapping && periodoVinculos.length > 0) {
        const resultadosAtualizacaoPeriodos = await Promise.all(
          periodoVinculos
            .filter((vinculo) => !vinculo.migrar)
            .map(async (vinculo) => {
            const periodoId = vinculo.selectedPeriodoId;

            if (!periodoId) {
              return {
                periodoUsuarioId: vinculo.periodoUsuarioId,
                success: false,
              };
            }

            const resultado = await atualizarPeriodoUsuario(
              vinculo.periodoUsuarioId,
              { periodoId }
            );

            return {
              periodoUsuarioId: vinculo.periodoUsuarioId,
              success: !!resultado,
            };
            })
        );

        const falhas = resultadosAtualizacaoPeriodos.filter(
          (resultado) => !resultado.success
        );

        if (falhas.length > 0) {
          setSaveError(
            `A cota foi ajustada, mas ${falhas.length} vinculo(s) de periodo nao foram salvos.`
          );
          return;
        }
      }

      onSuccess();
    } catch (error) {
      console.error(error);
      setSaveError("Nao foi possivel salvar o ajuste desta CTU.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="Ajustar CTU"
      open={open}
      onCancel={onCancel}
      onOk={handleSalvar}
      okText="Salvar ajuste"
      cancelText="Cancelar"
      confirmLoading={isSaving}
      okButtonProps={{ disabled: !canSubmit }}
      destroyOnClose
      width={1000}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {irregularidade ? (
          <Typography.Text type="secondary">
            Usuario: {irregularidade.nomeUsuario} | CTU ID:{" "}
            {irregularidade.cotaUsuarioId}
          </Typography.Text>
        ) : null}

        {loadError ? <Alert type="error" showIcon message={loadError} /> : null}
        {saveError ? <Alert type="error" showIcon message={saveError} /> : null}

        {isInitialLoading ? (
          <Skeleton active paragraph={{ rows: 12 }} />
        ) : cotaUsuarioAtual ? (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={isAjustePeu ? 24 : 12}>
              <Card title="Cota atual" size="small">
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Nome empreendimento">
                    {infoValue(empreendimentoAtual?.nome)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nome apartamento">
                    {infoValue(apartamentoAtual?.nome)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cronograma">
                    {formatCronogramaValue(empreendimentoAtual?.cronograma)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Capacidade">
                    {infoValue(apartamentoAtual?.capacidade)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Quartos">
                    {infoValue(apartamentoAtual?.quartos)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Banheiros">
                    {infoValue(apartamentoAtual?.banheiros)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Metragem">
                    {infoValue(apartamentoAtual?.metragem)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Numero da cota">
                    {infoValue(
                      cotaUsuarioAtual.CotaTable?.numeroCota ??
                        irregularidade?.numeroCota
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            {!isAjustePeu ? (
              <Col xs={24} lg={12}>
                <Card title="Nova cota" size="small">
                  <Form layout="vertical">
                    <Form.Item label="Empreendimento">
                      <Select
                        showSearch
                        allowClear
                        placeholder="Selecione um empreendimento"
                        optionFilterProp="label"
                        value={selectedEmpreendimentoId}
                        onChange={(value) => {
                          setSelectedEmpreendimentoId(value);
                          setLoadError(null);
                          setSaveError(null);
                        }}
                        options={empreendimentos.map((empreendimento) => ({
                          label:
                            empreendimento.nome ??
                            `Empreendimento ${empreendimento.id}`,
                          value: empreendimento.id,
                        }))}
                      />
                    </Form.Item>

                    <Form.Item label="Apartamento">
                      <Select
                        showSearch
                        allowClear
                        placeholder={
                          selectedEmpreendimentoId
                            ? "Selecione um apartamento"
                            : "Selecione um empreendimento primeiro"
                        }
                        optionFilterProp="label"
                        loading={isApartamentosLoading}
                        disabled={!selectedEmpreendimentoId}
                        value={selectedApartamentoId}
                        onChange={(value) => {
                          setSelectedApartamentoId(value);
                          setLoadError(null);
                          setSaveError(null);
                        }}
                        options={apartamentos.map((apartamento) => ({
                          label: apartamento.nome ?? `Apartamento ${apartamento.id}`,
                          value: apartamento.id,
                        }))}
                      />
                    </Form.Item>

                    <Form.Item label="Numero da cota">
                      <Select
                        showSearch
                        allowClear
                        placeholder={
                          selectedApartamentoId
                            ? "Selecione uma cota"
                            : "Selecione um apartamento primeiro"
                        }
                        optionFilterProp="label"
                        loading={isCotasLoading}
                        disabled={!selectedApartamentoId}
                        value={selectedCotaId}
                        onChange={(value) => {
                          setSelectedCotaId(value);
                          setSaveError(null);
                        }}
                        options={cotas.map((cota) => ({
                          label: `Cota ${cota.numeroCota}`,
                          value: cota.id,
                        }))}
                      />
                    </Form.Item>
                  </Form>

                  {apartamentoSelecionado || cotaSelecionada ? (
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Nome empreendimento">
                        {infoValue(empreendimentoSelecionado?.nome)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Nome apartamento">
                        {infoValue(apartamentoSelecionado?.nome)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Cronograma">
                        {formatCronogramaValue(
                          empreendimentoSelecionado?.cronograma
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Capacidade">
                        {infoValue(apartamentoSelecionado?.capacidade)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Quartos">
                        {infoValue(apartamentoSelecionado?.quartos)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Banheiros">
                        {infoValue(apartamentoSelecionado?.banheiros)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Metragem">
                        {infoValue(apartamentoSelecionado?.metragem)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Numero da cota">
                        {infoValue(cotaSelecionada?.numeroCota)}
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Selecione empreendimento, apartamento e cota para visualizar o destino."
                    />
                  )}
                </Card>
              </Col>
            ) : null}
          </Row>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Nenhum dado da CTU atual foi encontrado para ajuste."
          />
        )}

        {shouldRequirePeriodoMapping ? (
          <Card size="small">
            <Space
              direction="horizontal"
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Typography.Text strong></Typography.Text>
              <Button
                type={showPeriodosSection ? "primary" : "default"}
                disabled={!isAjustePeu}
                onClick={() => {
                  setShowPeriodosSection((currentValue) => !currentValue);
                  setSaveError(null);
                }}
              >
                Periodos
              </Button>
            </Space>
          </Card>
        ) : null}

        {shouldBypassPeriodoAdjustment && selectedCotaId ? (
          <Alert
            type="info"
            showIcon
            message="Este empreendimento nao possui cronograma fixo. Os periodos serao ajustados automaticamente pelo backend ao salvar."
          />
        ) : null}

        {shouldRequirePeriodoMapping && shouldShowPeriodosSection ? (
          <Card
            title="Ajustar periodos"
            size="small"
            loading={isPeriodosUsuarioLoading || isPeriodosNovaCotaLoading}
          >
            {!isPeriodosUsuarioLoading &&
            !isPeriodosNovaCotaLoading &&
            periodosUsuario.length === 0 ? (
              <Alert
                type="info"
                showIcon
                message="Esta CTU nao possui periodos de usuario para vincular."
              />
            ) : null}
            

            {!isPeriodosUsuarioLoading &&
            !isPeriodosNovaCotaLoading &&
            periodosUsuario.length > 0 &&
            periodosNovaCota.length === 0 ? (
              <Alert
                type="info"
                showIcon
                message="A nova cota nao possui periodos disponiveis para vinculo."
              />
            ) : null}

            {!isPeriodosUsuarioLoading &&
            !isPeriodosNovaCotaLoading &&
            periodosUsuario.length > 0 &&
            periodoVinculos.length > 0 ? (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Typography.Text type="secondary">
                  Vincule cada periodo antigo a um periodo da nova cota.
                </Typography.Text>

                {periodoVinculos.map((vinculo, index) => {
                  const periodoSelecionado = vinculo.selectedPeriodoId
                    ? periodosNovaCotaById[vinculo.selectedPeriodoId]
                    : undefined;
                  const hasDuplicate =
                    !!vinculo.selectedPeriodoId &&
                    duplicatedPeriodoIds.has(vinculo.selectedPeriodoId);

                  return (
                    <Card
                      key={vinculo.periodoUsuarioId}
                      size="small"
                      bodyStyle={{ padding: 16 }}
                    >
                      <Row gutter={[16, 12]} align="middle">
                        <Col xs={24} md={6}>
                          <Typography.Text strong>
                            Periodo antigo
                          </Typography.Text>
                          <div>
                            {vinculo.antigoCheckIn} a {vinculo.antigoCheckOut}
                          </div>
                        </Col>

                        <Col xs={24} md={8}>
                          <Typography.Text strong>
                            Novo periodo
                          </Typography.Text>
                          <Select
                            showSearch
                            allowClear
                            style={{ width: "100%", marginTop: 4 }}
                            placeholder="Selecione o novo periodo"
                            optionFilterProp="label"
                            disabled={!!vinculo.migrar}
                            value={vinculo.selectedPeriodoId}
                            onChange={(value) =>
                              handlePeriodoChange(
                                vinculo.periodoUsuarioId,
                                value
                              )
                            }
                            options={periodosNovaCota.map((periodo) => {
                              const periodoId = periodo.id;
                              const isSelectedInAnotherRow =
                                !!periodoId &&
                                periodoVinculos.some(
                                  (item) =>
                                    item.periodoUsuarioId !==
                                      vinculo.periodoUsuarioId &&
                                    !item.migrar &&
                                    item.selectedPeriodoId === periodoId
                                );

                              return {
                                label: `${formatDateValue(
                                  periodo.checkIn
                                )} a ${formatDateValue(periodo.checkOut)}`,
                                value: periodoId,
                                disabled: isSelectedInAnotherRow,
                              };
                            })}
                          />
                        </Col>

                        <Col xs={24} md={6}>
                          <Typography.Text strong>
                            Periodo vinculado
                          </Typography.Text>
                          <div style={{ marginTop: 4 }}>
                            {periodoSelecionado
                              ? `${formatDateValue(
                                  periodoSelecionado.checkIn
                                )} a ${formatDateValue(
                                  periodoSelecionado.checkOut
                                )}`
                              : "-"}
                          </div>
                        </Col>

                        <Col xs={24} md={4}>
                          <Typography.Text strong>Migrar</Typography.Text>
                          <div style={{ marginTop: 8 }}>
                            <Checkbox
                              checked={!!vinculo.migrar}
                              disabled={!vinculo.periodoAtualId}
                              onChange={(event) =>
                                handleMigrarChange(
                                  vinculo.periodoUsuarioId,
                                  event.target.checked
                                )
                              }
                            >
                              Migrar
                            </Checkbox>
                          </div>
                        </Col>
                      </Row>

                      {hasDuplicate ? (
                        <>
                          <Divider style={{ margin: "12px 0" }} />
                          <Alert
                            type="error"
                            showIcon
                            message="Este periodo novo ja foi escolhido em outra linha."
                          />
                        </>
                      ) : null}

                      {!vinculo.selectedPeriodoId && !vinculo.migrar ? (
                        <>
                          <Divider style={{ margin: "12px 0" }} />
                          <Alert
                            type="warning"
                            showIcon
                            message={`Selecione o novo periodo para o vinculo ${index + 1}.`}
                          />
                        </>
                      ) : null}

                      {vinculo.migrar ? (
                        <>
                          <Divider style={{ margin: "12px 0" }} />
                          <Alert
                            type="info"
                            showIcon
                            message="Este periodo sera migrado para a cota da CTU ao salvar."
                          />
                        </>
                      ) : null}
                    </Card>
                  );
                })}
              </Space>
            ) : null}
          </Card>
        ) : null}

        {selectedEmpreendimentoId &&
        !isApartamentosLoading &&
        apartamentos.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="Nenhum apartamento disponivel para o empreendimento selecionado."
          />
        ) : null}

        {selectedApartamentoId && !isCotasLoading && cotas.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="Nenhuma cota disponivel para o apartamento selecionado."
          />
        ) : null}
      </Space>
    </Modal>
  );
}
